<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="center" class="mb-4">
      <v-col cols="12" lg="auto" class="d-flex align-center">
        <report-meals-select-company></report-meals-select-company>
      </v-col>
      <v-col cols="12" lg="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
      <v-col cols="12" lg="auto" class="d-flex align-center">
        <v-switch
          v-model="includeBranches"
          :label="$t('more_restaurants_report.whole_company')"
          color="primary"
          inset
          dense
          hide-details
          class="mt-0"
        ></v-switch>
      </v-col>
    </v-row>

    <v-data-table
      :headers="getMobileHeaders(headers)"
      :items="rows"
      item-key="rowKey"
      class="striped-table ordera-box-shadow"
      dense
      hide-default-footer
      disable-pagination
      disable-sort
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <v-container class="text-center py-12">
          <v-icon size="64" color="grey lighten-1" class="mb-4"> mdi-account-off </v-icon>
          <div class="text-h6 grey--text text--darken-1 mb-2">
            {{ $t("no_employee_report_data") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("select_date_and_filters") }}
          </div>
        </v-container>
      </template>
      <template v-slot:item.worker="{ item }">
        <span v-if="item.showWorker">{{ item.worker }}</span>
      </template>
      <template v-slot:item.date="{ item }">{{ item.dateText }}</template>
      <template v-slot:item.shift="{ item }">{{ formatShift(item.shift) }}</template>
      <template v-slot:item.item="{ item }">{{ toLatin(item.item) }}</template>
      <template v-slot:item.combination="{ item }">
        {{ item.combination ? toLatin(item.combination) : "" }}
      </template>
      <template v-slot:body.append v-if="rows.length">
        <tr class="font-weight-bold totals-row">
          <td
            v-for="header in headers"
            :key="header.value"
            :class="header.align === 'right' ? 'text-right' : header.class"
          >
            {{ totalCell(header.value) }}
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import moment from "moment";
import reportService from "@/services/report";
import ReportMealsSelectCompany from "@/components/report/more-restaurants/fields/ReportMealsSelectCompanyField.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import { SHIFTS } from "@/constants/shifts";

const sum = (items, key) => items.reduce((total, item) => total + (Number(item[key]) || 0), 0);

/**
 * Kartica „Radnici – po stavkama”: ko je šta naručio po danu i smjeni (korisnici-items).
 * Postojeći zbirni pregled radnika ostaje u kartici „Radnici”.
 */
export default {
  name: "ReportMealsTabEmployeeItems",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders],
  components: {
    ReportMealsSelectCompany,
  },
  data: () => ({
    loading: false,
    items: [],
    total: null,
    showAmount: false,
    includeBranches: false,
    selectedShifts: SHIFTS.map((shift) => shift.id),
  }),
  computed: {
    selectedCompany() {
      return this.$store.state.report.selectedMealCompany;
    },
    filters() {
      return this.$store.state.report.filters;
    },
    /** Ime radnika samo u prvom redu uzastopnih redova istog radnika. */
    rows() {
      return this.items.map((item, index) => {
        const worker = [item.name, item.lastname]
          .filter(Boolean)
          .map((part) => this.toLatin(part))
          .join(" ");
        const previous = this.items[index - 1];
        const sameAsPrevious =
          previous && (previous.user_id ?? previous.id) === (item.user_id ?? item.id) &&
          previous.name === item.name &&
          previous.lastname === item.lastname;
        return {
          ...item,
          rowKey: index,
          worker,
          showWorker: !sameAsPrevious,
          dateText:
            item.date && moment(item.date).isValid()
              ? moment(item.date).format("DD.MM.YYYY")
              : item.date || "",
        };
      });
    },
    headers() {
      return [
        { text: this.$t("worker"), value: "worker" },
        { text: this.$t("date"), value: "date" },
        { text: this.$t("shifts"), value: "shift" },
        { text: this.$t("more_restaurants_report.item"), value: "item" },
        { text: this.$t("more_restaurants_report.quantity_short"), value: "quantity", align: "right" },
        { text: this.$t("more_restaurants_report.package_items"), value: "combination" },
        ...(this.showAmount
          ? [{ text: this.$t("amount"), value: "amount", align: "right" }]
          : []),
        { text: this.$t("taken"), value: "quantity_payed", align: "right" },
      ];
    },
    /** Ukupno: iz odgovora (`total`), a što nedostaje sabere se iz redova. */
    totals() {
      const fromResponse =
        this.total && typeof this.total === "object" ? this.total : {};
      return {
        quantity: fromResponse.quantity ?? sum(this.items, "quantity"),
        amount: fromResponse.amount ?? fromResponse.total ?? sum(this.items, "amount"),
        quantity_payed: fromResponse.quantity_payed ?? sum(this.items, "quantity_payed"),
      };
    },
  },
  methods: {
    formatShift(shift) {
      if (Number.isInteger(shift)) return shift;
      return typeof shift === "string" ? shift.replace("-smena", "") : "";
    },
    totalCell(key) {
      if (key === "worker") return this.$t("totalU");
      return Object.prototype.hasOwnProperty.call(this.totals, key) ? this.totals[key] : "";
    },
    async load() {
      if (!this.selectedCompany || !this.selectedCompany.id) {
        this.items = [];
        this.total = null;
        return;
      }
      this.loading = true;
      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
      };
      if (this.selectedShifts.length && this.selectedShifts.length < SHIFTS.length) {
        params.shifts = [...this.selectedShifts].sort().join(",");
      }
      if (this.includeBranches) {
        params.include_branches = 1;
      }
      try {
        const { data } = await reportService.getCompanyMoreRestaurantsKorisniciItems(
          this.selectedCompany.id,
          params
        );
        this.items = Array.isArray(data?.items) ? data.items : [];
        this.total = data?.total ?? null;
        this.showAmount = Boolean(data?.show_amount);
      } catch (error) {
        this.items = [];
        this.total = null;
        this.showErrorSnackbar(this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
    /** Excel/štampa: ime radnika u svakom redu (zbog filtriranja), na kraju red Ukupno. */
    exportRows() {
      const rows = this.items.map((item) => ({
        ...item,
        worker: [item.name, item.lastname]
          .filter(Boolean)
          .map((part) => this.toLatin(part))
          .join(" "),
        date:
          item.date && moment(item.date).isValid()
            ? moment(item.date).format("DD.MM.YYYY")
            : item.date || "",
        shift: this.formatShift(item.shift),
        item: this.toLatin(item.item || ""),
        combination: item.combination ? this.toLatin(item.combination) : "",
      }));
      const totalRow = { worker: this.$t("totalU") };
      this.headers.forEach((header) => {
        if (header.value !== "worker") {
          totalRow[header.value] = this.totalCell(header.value);
        }
      });
      return [...rows, totalRow];
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.load();
      },
      deep: true,
    },
    selectedCompany: {
      handler() {
        this.load();
      },
      immediate: true,
      deep: true,
    },
    selectedShifts() {
      this.load();
    },
    includeBranches() {
      this.load();
    },
    isPrinting() {
      if (!this.isPrinting) return;
      this.printDocument({
        title: [this.$t("report"), this.$t("more_restaurants_report.worker_items_tab")].join(": "),
        tables: [{ headers: this.headers, items: this.exportRows(), showCaption: true }],
        hideDatetime: true,
        subtitle: this.subtitle,
      });
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      const title = [this.$t("report"), this.$t("more_restaurants_report.worker_items_tab")].join(" ");
      this.downloadExcel({
        fileName: title + ".xlsx",
        sheets: [
          {
            name: this.$t("employees"),
            headers: this.headers,
            items: this.exportRows(),
            title,
            subtitle: this.subtitle,
          },
        ],
      });
    },
  },
};
</script>
